import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { trackSignupInitiated, trackSignupCompleted, trackLoginInitiated, trackLoginCompleted } from "@/lib/analytics";
import { consumeLandingOrigin, logLandingAttribution } from "@/lib/landing-events";
import { getPlatform, isNativePlatform } from "@/lib/platform";
import { InAppBrowser, DefaultSystemBrowserOptions } from '@capacitor/inappbrowser';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Seo } from "@/components/Seo";

import authBg from "@/assets/auth-bg3-1.webp";
import logoWhite from "@/assets/logo-white.png";

const AppleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function Auth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [termsAndPrivacyAgreed, setTermsAndPrivacyAgreed] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/designer");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        // Set signup_platform for OAuth users (trigger can't capture it for OAuth flows)
        supabase.from('profiles')
          .update({ signup_platform: getPlatform() })
          .eq('id', session.user.id)
          .is('signup_platform', null)
          .then(() => {});
        navigate("/designer");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!termsAndPrivacyAgreed) {
      toast({
        variant: "destructive",
        title: "Consent Required",
        description: "Please agree to the Privacy Policy and Terms of Service.",
      });
      return;
    }

    setLoading(true);
    trackSignupInitiated();

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            signup_platform: getPlatform(),
          },
          emailRedirectTo: `${window.location.origin}/designer`,
        },
      });

      if (error) throw error;

      if (data.user) {
        trackSignupCompleted(data.user.id);

        // Attribute signup to a region/topic landing page if the visitor arrived via one.
        const landingOrigin = consumeLandingOrigin();
        if (landingOrigin) {
          logLandingAttribution(landingOrigin, data.user.id);
        }


        // Record consents in database
        await supabase.functions.invoke("record-consent", {
          body: {
            consents: [
              { type: "age_verified", given: true, version: "v1.0" },
              { type: "terms", given: true, version: "v1.0" },
              { type: "privacy", given: true, version: "v1.0" },
              { type: "marketing", given: marketingConsent, version: "v1.0" },
            ],
          },
        });

        // Update profile with age verification and marketing consent
        await supabase
          .from("profiles")
          .update({
            age_verified: true,
            marketing_emails_consent: marketingConsent,
          })
          .eq("id", data.user.id);

      }

      toast({
        title: "Account created!",
        description: "You can now start designing.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    trackLoginInitiated();

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        trackLoginCompleted(data.user.id);
      }

      toast({
        title: "Welcome back!",
        description: "Successfully signed in.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    trackLoginInitiated();

    try {
      const platform = getPlatform();
      const isNative = platform === "android" || platform === "ios";
      let redirectTo: string;

      if (isNative) {
        redirectTo = "https://decorcanvas.design/oauth-callback";
      } else {
        redirectTo = `${window.location.origin}/designer`;
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: isNative,
        },
      });

      if (error) throw error;

      if (isNative && data?.url) {
        await InAppBrowser.openInSystemBrowser({ url: data.url, options: DefaultSystemBrowserOptions });
      }
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    trackLoginInitiated();

    try {
      const platform = getPlatform();
      const isNative = platform === "android" || platform === "ios";
      const hostname = window.location.hostname;

      console.log("[AppleAuth] Starting Apple sign-in", { platform, isNative, hostname, origin: window.location.origin });

      // Environment guardrail: warn on unsupported preview surfaces
      const isPreviewWrapper = hostname.startsWith("preview--") || hostname.startsWith("id-preview--");
      if (!isNative && isPreviewWrapper) {
        toast({
          variant: "destructive",
          title: "Apple Sign-In Unavailable",
          description: "Apple Sign-In is not supported on preview URLs. Please use the published app URL instead.",
        });
        setLoading(false);
        return;
      }

      if (isNative && platform === 'ios') {
        // Use native Apple Sign-In dialog (no browser needed)
        await SocialLogin.initialize({ apple: { clientId: 'com.atheniaai.decorcanvas.login' } });
        
        const result = await SocialLogin.login({ 
          provider: 'apple', 
          options: { scopes: ['email', 'name'] } 
        });

        const identityToken = (result as any).result?.idToken;
        
        console.log("[AppleAuth] Native sign-in successful, exchanging token via edge function...");

        // Use edge function to verify token server-side (bypasses Client ID order issue)
        const { data: authData, error: authError } = await supabase.functions.invoke('apple-native-auth', {
          body: { id_token: identityToken },
        });

        if (authError || !authData?.verification_token) {
          const errorMsg = authData?.error || authError?.message || 'Unknown error';
          console.error("[AppleAuth] Edge function failed:", errorMsg);
          throw new Error(`Apple Sign-In failed: ${errorMsg}`);
        }

        console.log("[AppleAuth] Edge function returned token, completing sign-in...");

        // Complete sign-in with the magic link token
        const { data: signInData, error: signInError } = await supabase.auth.verifyOtp({
          token_hash: authData.verification_token,
          type: 'magiclink',
        });

        if (signInError) {
          console.error("[AppleAuth] OTP verification failed:", signInError.message);
          throw new Error(`Apple Sign-In verification failed: ${signInError.message}`);
        }
        
        console.log("[AppleAuth] Sign-in successful, user:", signInData.user?.id);
        // onAuthStateChange handles navigation
      } else {
        // Web Apple Sign-In — bypass auth-bridge, redirect manually
        console.log("[AppleAuth] Starting web OAuth flow", { origin: window.location.origin });

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo: `${window.location.origin}/designer`,
            skipBrowserRedirect: true,
          },
        });

        if (error) {
          console.error("[AppleAuth] OAuth error:", error.message);
          throw error;
        }

        if (data?.url) {
          window.location.href = data.url;
        }
      }
    } catch (error: any) {
      const msg = (error?.message || '').toLowerCase();
      const code = error?.code;
      const isCancellation =
        code === 1000 ||
        code === '1000' ||
        msg.includes('cancel') ||
        msg.includes('user cancelled') ||
        msg.includes('user canceled') ||
        msg.includes('asauthorizationerror');

      if (!isCancellation) {
        console.error("[AppleAuth] Final error:", { message: error.message, code: error.code, stack: error.stack });
        toast({
          variant: "destructive",
          title: "Sign in failed",
          description: error.message || "Apple Sign-In failed. Please try again or use another sign-in method.",
        });
      }
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    // Reset form fields when toggling
    setEmail("");
    setPassword("");
    setFullName("");
    setTermsAndPrivacyAgreed(false);
    setMarketingConsent(true);
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      <Seo title="Sign in — Decor Canvas" description="Sign in or create your Decor Canvas account." canonicalPath="/auth" noindex />
      {/* Background Image */}
      <img 
        src={authBg}
        alt="" 
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-[79%_80%] sm:object-[50%_13%]"
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/25" />
      
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-6">
        <div className="w-full max-w-md backdrop-blur-sm bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
          {/* Logo */}
          <img 
            src={logoWhite} 
            alt="Decor Canvas" 
            className="w-16 h-16 mx-auto mb-2"
          />
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-white text-center mb-8">
            {isSignUp ? "Sign Up" : "Sign In"}
          </h1>
          
          {/* Form */}
          <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
            {/* Full Name - Sign Up only */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-white font-semibold text-sm">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Alex John"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="bg-white border-0 rounded-xl h-12 text-gray-800 placeholder:text-gray-400"
                />
              </div>
            )}
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white font-semibold text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="example@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-0 rounded-xl h-12 text-gray-800 placeholder:text-gray-400"
              />
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white font-semibold text-sm">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={isSignUp ? 6 : undefined}
                  className="bg-white border-0 rounded-xl h-12 text-gray-800 placeholder:text-gray-400 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            {/* Consent Checkboxes - Sign Up only */}
            {isSignUp && (
              <div className="space-y-4 pt-4">
                {/* Required: Terms & Privacy */}
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="terms-privacy"
                    checked={termsAndPrivacyAgreed}
                    onCheckedChange={(checked) => setTermsAndPrivacyAgreed(checked as boolean)}
                    className="mt-1 border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-gray-900"
                  />
                  <Label htmlFor="terms-privacy" className="text-white/90 text-sm cursor-pointer font-normal leading-relaxed">
                    I agree to the{" "}
                    <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link>
                    {" "}and{" "}
                    <Link to="/terms" className="underline hover:text-white">Terms of Service</Link>
                  </Label>
                </div>
                
                {/* Optional: Marketing */}
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="marketing"
                    checked={marketingConsent}
                    onCheckedChange={(checked) => setMarketingConsent(checked as boolean)}
                    className="mt-1 border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-gray-900"
                  />
                  <Label htmlFor="marketing" className="text-white/90 text-sm cursor-pointer font-normal leading-relaxed">
                    I want to receive product updates and marketing emails
                    <span className="text-white/60 ml-1">(optional)</span>
                  </Label>
                </div>
              </div>
            )}
            
            {/* Primary Button */}
            <Button 
              type="submit"
              className="w-full bg-[#E38D5B] hover:bg-[#E38D5B]/90 text-black rounded-xl h-12 font-semibold text-base mt-6"
              disabled={loading || (isSignUp && !termsAndPrivacyAgreed)}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSignUp ? "Create Account" : "Sign In"}
            </Button>
          </form>
          
          {/* Divider & Social Buttons */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/30" />
            <span className="text-white/70 text-sm">or</span>
            <div className="flex-1 h-px bg-white/30" />
          </div>
          
          {/* Google - hidden on iOS */}
          {getPlatform() !== 'ios' && (
            <Button 
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-100 text-gray-800 border-0 rounded-xl h-12 font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
              <span className="ml-2">Continue with Google</span>
            </Button>
          )}

          {/* Apple - shown on web + iOS */}
          {getPlatform() !== 'android' && (
            <Button 
              type="button"
              variant="outline"
              onClick={handleAppleSignIn}
              disabled={loading}
              className="w-full bg-black hover:bg-black/90 text-white border-0 rounded-xl h-12 font-semibold mt-3"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleIcon />}
              <span className="ml-2">Continue with Apple</span>
            </Button>
          )}
          
          {/* Toggle Link */}
          <p className="text-center text-white/90 mt-6">
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <button 
              type="button"
              onClick={toggleMode} 
              className="font-semibold text-white hover:underline"
            >
              {isSignUp ? "Sign In" : "Sign Up"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
